import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { saveUTMsToCookies } from '@landr/core';
import useCustomEventController from 'controllers/CustomEvent';
import { useAnalyticsIdentityController } from 'controllers/AnalyticsIdentity';
import { usePostAuthenticationActionController } from 'controllers/PostAuthenticationAction';
import { useActivePlayerContext } from 'contexts/ActivePlayerContext/ActivePlayerContext';
import { useClientUpdateContext } from 'contexts/ClientUpdateContext/ClientUpdateContext';
import { useErrorBoundaryContext } from 'contexts/ErrorBoundaryContext/ErrorBoundaryContext';
import { useUserProfileContext } from 'contexts/UserProfileContext/UserProfileContext';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';
import { useDeduplicateParams } from 'hooks/useDeduplicateParams';
import { useShowPoorStanding } from 'hooks/useShowPoorStanding';
import { useDisableIosTextFieldZoom } from 'hooks/useDisableIosTextFieldZoom';
import useMountEffect from 'hooks/useMountEffect';
import { sendBrowse } from 'helpers/dawPlugin';
import { setCookie, deleteCookie, getIsDAWPlugin } from 'helpers';
import { UTMCookieSettings } from './constants/UTMCookieSettings';

// Set stable authentication status cookie for Unbounce use
const toggleCookie = (isAuthenticated: boolean): void => {
    if (isAuthenticated) {
        setCookie('isAuthenticated', '');
        deleteCookie('isGuestUser');
    } else {
        setCookie('isGuestUser', '');
        deleteCookie('isAuthenticated');
    }
};

const useApp = () => {
    const location = useLocation();
    const isDAWPlugin = getIsDAWPlugin();
    const { userProfile } = useUserProfileContext();

    // STATE
    const [loaderDisplayed, setLoaderDisplayed] = useState(false);

    // CALLBACKS
    const onFallbackLoad = useCallback(
        (): void => setLoaderDisplayed(true),
        [],
    );
    const onFallbackUnload = useCallback(
        (): void => setLoaderDisplayed(false),
        [],
    );

    // CONTEXT
    const { stopActivePlayer } = useActivePlayerContext();

    const { setUpdateRequired } = useClientUpdateContext();

    const { error } = useErrorBoundaryContext();

    const {
        appState,
        isAuthenticated,
        isLoading: isAuthenticationLoading,
        continueOrRedirectToProfileSurvey,
        isSignup,
    } = useAuthentication();

    // CONTROLLERS
    useAnalyticsIdentityController({
        isAuthenticated,
        isAuthenticationLoading,
        userProfile,
        continueOrRedirectToProfileSurvey,
    });

    useCustomEventController({
        stopActivePlayer,
        setUpdateRequired,
    });

    usePostAuthenticationActionController({
        appState,
        isAuthenticated,
        isAuthenticationLoading,
        isSignup,
    });

    // CUSTOM HOOKS
    useShowPoorStanding();

    // EFFECTS
    useMountEffect(() => {
        saveUTMsToCookies(UTMCookieSettings);
    });

    useDeduplicateParams();

    // To avoid bug https://mixgenius.atlassian.net/browse/SP-4106
    useDisableIosTextFieldZoom();

    useEffect(() => {
        // Propagate to plugin
        if (isDAWPlugin) {
            sendBrowse(location);
        }
    }, [isDAWPlugin, location]);

    useEffect(() => {
        toggleCookie(isAuthenticated);
    }, [isAuthenticated]);

    return {
        isDAWPlugin,
        isAuthenticated,
        loaderDisplayed,
        isAuthenticationLoading,
        onFallbackLoad,
        onFallbackUnload,
        error,
    };
};

export default useApp;
